<script lang="ts">
	/**
	 * Worker heartbeats and queue depth (Design §3.2, §13).
	 *
	 * Both panels are fed by one `getOperationsHealth` call, which is why they live in one
	 * component: they are two readings of the same instrument, and fetching it twice would only
	 * make them able to disagree.
	 *
	 * The reason this screen exists at all: **worker failure is invisible from the storefront.**
	 * §6.2's inline reconcile keeps checkout succeeding whatever the workers are doing, so
	 * purchases complete, receipts look fine, and meanwhile renewals stop charging, entitlements
	 * stop reaching applications, email stops leaving and reconciliation stops running. No
	 * customer complaint arrives for weeks. `isStale` is the only signal, so it is stated in
	 * words — a red dot is not a sentence, and an operator scanning a screen in a hurry, or one
	 * who cannot distinguish red from green, reads the words.
	 */

	import { onMount } from 'svelte';
	import { Badge, Button, Table, TableBody, TableBodyCell, TableBodyRow, TableHead, TableHeadCell } from 'flowbite-svelte';
	import { EmptyState, Panel, RelativeDate } from '../../../components';
	import { getOperationsHealth } from '../../../AppNetwork';
	import type { OperationsHealth } from '../../../AppTypes';

	let health: OperationsHealth | null = null;
	let loading: boolean = true;
	let loadError: string = '';

	async function fnLoad(): Promise<void> {
		loading = true;
		loadError = '';
		try {
			health = await getOperationsHealth();
		} catch (error) {
			loadError = (error as Error).message;
		} finally {
			loading = false;
		}
	}

	$: staleWorkers = (health?.workers ?? []).filter(worker => worker.isStale);

	$: queueRows = health === null ? [] : [
		{
			label: 'Oldest unprocessed provider event',
			date: health.events.oldestUnprocessedAt,
			empty: 'Nothing waiting',
			note: 'Every webhook the provider sent that has not been applied yet.'
		},
		{
			label: 'Oldest pending delivery',
			date: health.deliveries.oldestPendingAt,
			empty: 'Nothing waiting',
			note: 'Entitlement state an application has not received yet.'
		},
		{
			label: 'Oldest pending email',
			date: health.email.oldestPendingAt,
			empty: 'Nothing waiting',
			note: 'Receipts and dunning notices still in the outbox.'
		}
	];

	$: queueCounts = health === null ? [] : [
		{ label: 'Pending deliveries', value: health.deliveries.pending, icon: 'paper-plane' },
		{ label: 'Dead deliveries', value: health.deliveries.dead, icon: 'skull-crossbones' },
		{ label: 'Failed provider events', value: health.events.failed, icon: 'triangle-exclamation' },
		{ label: 'Pending emails', value: health.email.pending, icon: 'envelope' },
		{ label: 'Failed emails', value: health.email.failed, icon: 'envelope-circle-check' }
	];

	onMount(() => fnLoad());
</script>

<Panel title="Worker heartbeats" faIcon="heart-pulse">
	<svelte:fragment slot="titleRight">
		<Button size="xs" color="alternative" on:click={() => fnLoad()}>
			<i class="fa-solid fa-arrows-rotate mr-2" />Refresh
		</Button>
	</svelte:fragment>

	<div class="space-y-4">
		<p class="text-default text-sm">
			Worker failure is <strong>invisible from the storefront</strong>. Checkout reconciles
			inline, so purchases keep succeeding exactly as before, while renewals stop charging,
			entitlements stop reaching applications, email stops going out and reconciliation stops
			running. Nothing else on this screen, and no customer, will tell you. These heartbeats will.
		</p>

		{#if loading}
			<EmptyState variant="loading" title="Loading the heartbeats…" />
		{:else if loadError.length > 0}
			<EmptyState variant="error" title="The worker health could not be loaded." description={loadError}>
				<Button color="alternative" size="sm" on:click={() => fnLoad()}>Try again</Button>
			</EmptyState>
		{:else if health === null || health.workers.length === 0}
			<EmptyState
				variant="error"
				faIcon="heart-crack"
				title="No worker has ever reported."
				description="Nothing is running the background work. Renewals, entitlement delivery, email and reconciliation are all stopped, and the storefront will not show it."
			/>
		{:else}
			{#if staleWorkers.length > 0}
				<!-- Said in words, not in colour: the badge below repeats it, it does not carry it. -->
				<div class="widget bg-depth border-widget border-amber-400 dark:border-amber-500">
					<div class="card-label"><i class="fa-solid fa-triangle-exclamation text-[11px] mr-1" />Stale workers</div>
					<p class="text-sm mt-1">
						<strong>{staleWorkers.length}</strong>
						of {health.workers.length}
						{health.workers.length === 1 ? 'worker is' : 'workers are'}
						<strong>stale</strong>: {staleWorkers.length === 1 ? 'it has' : 'they have'} not reported a run
						recently, so the work {staleWorkers.length === 1 ? 'it does' : 'they do'} is not being done:
						{staleWorkers.map(worker => worker.workerName).join(', ')}.
					</p>
				</div>
			{:else if !health.workersReporting}
				<div class="widget bg-depth border-widget border-amber-400 dark:border-amber-500">
					<div class="card-label"><i class="fa-solid fa-triangle-exclamation text-[11px] mr-1" />Not reporting</div>
					<p class="text-sm mt-1">No worker is reporting a heartbeat. Treat every background job as stopped.</p>
				</div>
			{/if}

			<div class="relative overflow-x-auto">
				<Table>
					<TableHead>
						<TableHeadCell>Worker</TableHeadCell>
						<TableHeadCell>Instance</TableHeadCell>
						<TableHeadCell>Last run</TableHeadCell>
						<TableHeadCell>Last claimed</TableHeadCell>
						<TableHeadCell>State</TableHeadCell>
						<TableHeadCell>Last error</TableHeadCell>
					</TableHead>
					<TableBody>
						{#each health.workers as worker (`${worker.workerName}-${worker.instanceID}`)}
							<TableBodyRow>
								<TableBodyCell class="dataTable font-medium">{worker.workerName}</TableBodyCell>
								<TableBodyCell class="dataTable font-mono text-xs break-all">{worker.instanceID}</TableBodyCell>
								<TableBodyCell class="dataTable"><RelativeDate date={worker.lastRunAt} /></TableBodyCell>
								<TableBodyCell class="dataTable">
									{#if worker.lastClaimedAt}
										<RelativeDate date={worker.lastClaimedAt} />
									{:else}
										<span class="text-default">Never claimed work</span>
									{/if}
								</TableBodyCell>
								<TableBodyCell class="dataTable">
									<!-- The word is the signal; the badge colour only repeats it. -->
									<Badge color={worker.isStale ? 'red' : 'green'}>
										{worker.isStale ? 'Stale' : 'Healthy'}
									</Badge>
									<div class="text-default text-xs mt-1">
										{worker.isStale
											? 'Stale. This worker has stopped reporting and its work is not being done.'
											: 'Healthy. Reporting on schedule.'}
									</div>
								</TableBodyCell>
								<TableBodyCell class="dataTable">
									{#if worker.lastError}
										<span class="text-default text-xs break-all">{worker.lastError}</span>
									{:else}
										<span class="text-default">—</span>
									{/if}
								</TableBodyCell>
							</TableBodyRow>
						{/each}
					</TableBody>
				</Table>
			</div>
		{/if}
	</div>
</Panel>

<Panel title="Queues" faIcon="layer-group">
	<div class="space-y-4">
		<p class="text-default text-sm">
			Queue depth is the second reading of the same instrument: a stopped worker shows up here
			as an <strong>age</strong> that keeps growing. Ages matter more than counts: a hundred
			items queued a second ago is a busy store, one item queued yesterday is a broken one.
		</p>

		{#if loading}
			<EmptyState variant="loading" title="Loading the queues…" />
		{:else if loadError.length > 0}
			<EmptyState variant="error" title="The queue depth could not be loaded." description={loadError}>
				<Button color="alternative" size="sm" on:click={() => fnLoad()}>Try again</Button>
			</EmptyState>
		{:else if health === null}
			<EmptyState title="No queue readings." />
		{:else}
			{#if health.email.unreceiptedSales}
				<div class="widget bg-depth border-widget border-amber-400 dark:border-amber-500">
					<div class="card-label"><i class="fa-solid fa-receipt text-[11px] mr-1" />Unreceipted sales</div>
					<p class="text-sm mt-1">
						Money has been taken with <strong>no email provider configured</strong>, so those
						customers have no receipt. Configure email, then the queued messages will go out.
					</p>
				</div>
			{/if}

			<div class="grid grid-cols-1 md:grid-cols-3 gap-3">
				{#each queueRows as row (row.label)}
					<div class="widget bg-depth">
						<div class="card-label">{row.label}</div>
						<div class="text-lg font-semibold mt-1">
							{#if row.date}
								<RelativeDate date={row.date} />
							{:else}
								<span class="text-default text-base">{row.empty}</span>
							{/if}
						</div>
						<p class="text-default text-xs mt-2">{row.note}</p>
					</div>
				{/each}
			</div>

			<div class="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">
				{#each queueCounts as counter (counter.label)}
					<div class="widget bg-depth">
						<div class="card-label">
							<i class="fa-solid fa-{counter.icon} text-[11px] mr-1 text-default" />{counter.label}
						</div>
						<div class="text-2xl font-semibold mt-1">{counter.value}</div>
					</div>
				{/each}
			</div>

			{#if !health.email.configured}
				<p class="text-default text-sm">
					No email provider is configured, so the email queue will not drain whatever the workers do.
				</p>
			{/if}
		{/if}
	</div>
</Panel>
